import { media } from "../../design/breakpoints";
import axios from "axios";
import React, { useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom";
import styled, { css, keyframes } from "styled-components";
import { GlobalState } from "../../GlobalState";
import ToggleButton from "../utils/ToggleButton";
import { editAccess } from "../utils/utils";
import { formatOfflineDuration } from "../utils/formatOfflineDuration";

const PbBlock = styled.div`
  position: relative;
  flex: 0 0 auto;
  width: 300px;
  margin: 0.5rem;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  display: flex;
  transition: all 0.3s ease;

  ${media.max480} {
    flex: 1 1 100%;
    max-width: 100%;
    min-width: 0;
    margin: 0.3rem 0;
  }
`;

const AccentBar = styled.div`
  width: 5px;
  flex-shrink: 0;
  background: ${({ $isFaulty, $isOnline }) =>
    $isFaulty ? "#F59E0B" : $isOnline ? "#22C55E" : "#94A3B8"};
`;

const PbInner = styled.div`
  flex: 1;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const ToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;

  ${({ $isFaulty, $isOnline }) =>
    $isFaulty
      ? css`filter: hue-rotate(-70deg) saturate(1.4);`
      : !$isOnline
      ? css`filter: grayscale(1) opacity(0.7);`
      : css`filter: none;`}
`;

const PbMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

const LocationName = styled.p`
  margin: 0;
  font-weight: 700;
  font-size: 0.85rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  color: #111827;

  ${media.max768} {
    font-size: 0.75rem;
  }
`;

const StatusLine = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  /* FIX #11: offline is clearly red, not the same grey as "OFF" */
  color: ${({ $isFaulty, $isOnline, $isOff }) =>
    $isFaulty
      ? "#D97706"
      : !$isOnline
      ? "#ef4444"          /* offline — red, visually distinct from OFF */
      : $isOff
      ? "#9CA3AF"          /* OFF (but online) — grey */
      : "#16A34A"};        /* ON running — green */

  ${media.max768} {
    font-size: 0.65rem;
  }
`;

const Badge = styled.span`
  font-size: 9.5px;
  font-weight: 500;
  padding: 2px 7px;
  border-radius: 8px;
  white-space: nowrap;
  flex-shrink: 0;
  background: ${({ $isFaulty, $isOnline }) =>
    $isFaulty
      ? "rgba(245,158,11,.12)"
      : $isOnline
      ? "rgba(34,197,94,.12)"
      : "#F3F4F6"};
  color: ${({ $isFaulty, $isOnline }) =>
    $isFaulty ? "#B45309" : $isOnline ? "#15803D" : "#090a0e"};
`;

const BadgeStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  flex-shrink: 0;
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.08); }
`;

const PlainDuration = styled.span`
  display: inline-block;
  font-size: 10px;
  font-weight: 500;
  color: #d81111;
  white-space: nowrap;
  animation: ${pulse} 2s infinite ease-in-out;
  transform-origin: center;
`;

const DummyWrapper = styled.div`
  position: absolute;
  z-index: 100;
  top: 0%;
  background-color: transparent;
  width: 60%;
  height: 100%;
  pointer-events: none;
`;

function toBool(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    return v === "1" || v === "true" || v === "on";
  }
  return Boolean(value);
}

export default function PumpStatusV2({ sensor_id, motor_id, isBorewell, onStatusReport }) {
  const history  = useHistory();
  const location = useLocation();
  const isDetailPage = location.pathname.includes("/motor/");
  const state = useContext(GlobalState);

  const [pumpState,    setPumpState]    = useState(false);
  const [pumpData,     setPumpData]     = useState({});
  const [sensorStatus, setSensorStatus] = useState("online");
  const [pumpExist,    setPumpExist]    = useState(true);

  const locationName = !pumpData?.motor_status?.location_name
    ? "Pump"
    : pumpData?.motor_status?.location_name;

  // FIX #9: increased MAX_LENGTH from 15 → 20
  const MAX_LENGTH = 20;
  const truncatedLocationName =
    locationName.length > MAX_LENGTH
      ? locationName.substring(0, MAX_LENGTH) + "…"
      : locationName;

  async function togglePumpStatus(motor_id) {
    const previousState = pumpState;
    try {
      setPumpState(!pumpState);
      await axios.get(
        `${process.env.REACT_APP_API}/sensor/v2/toggle_pump_status?motor_id=${motor_id}`,
        { headers: { Authorization: state.token[0] } }
      );
    } catch (err) {
      setPumpState(previousState);
      console.log(err?.response?.data?.message || err.message);
    }
  }

  async function fetchData(token, motor_id) {
    try {
      const pumpStatus = await axios.get(
        `${process.env.REACT_APP_API}/sensor/v2/pump_status?motor_id=${motor_id}`,
        { headers: { Authorization: token[0] } }
      );
      setPumpData(pumpStatus.data);
      setPumpState(toBool(pumpStatus.data.motor_status.motor_on));
      setSensorStatus(pumpStatus.data.sensor_status);
      // FIX #1: report live status to parent dashboard
      if (onStatusReport) {
        onStatusReport(motor_id, pumpStatus.data.sensor_status === "online");
      }
    } catch (err) {
      if (err?.response?.status === 404) setPumpExist(false);
      console.log(err?.response?.data?.message || err.message);
    }
  }

  useEffect(() => {
    fetchData(state.token, motor_id);
    const interval = setInterval(() => fetchData(state.token, motor_id), 5000);
    return () => clearInterval(interval);
  }, [state.token, motor_id]);

  if (!pumpExist) return <></>;

  const isOnline  = sensorStatus === "online";
  const isFaulty  = sensorStatus === "fault" || sensorStatus === "faulty";
  const isOff     = !pumpState;

  // FIX #11: clearer status labels distinguishing offline from OFF
  const statusLabel = isFaulty
    ? "FAULT · check sensor"
    : !isOnline
    ? "Offline"
    : pumpState
    ? "ON · running"
    : "OFF";

  return (
    <PbBlock
      /* Issue 5: hover tooltip shows name + offline status */
      title={
        !isOnline
          ? `${locationName} — ${isFaulty ? "faulty" : "offline"}`
          : undefined
      }
    >
      <DummyWrapper
        onClick={() => {
          if (!isBorewell) return;
          history.push(`/motor/${motor_id}/sensor/${sensor_id}/Pump - ${locationName}`);
        }}
      />

      <AccentBar $isOnline={isOnline} $isFaulty={isFaulty} />

      <PbInner>
        {!isDetailPage && (
          <ToggleWrapper $isOnline={isOnline} $isFaulty={isFaulty}>
            <ToggleButton
              disabled={!editAccess(state.userAPI) || state.userAPI?.role === "CUST_USER"}
              id={motor_id}
              checked={pumpState}
              onChange={() => togglePumpStatus(motor_id)}
            />
          </ToggleWrapper>
        )}

        <PbMeta>
          <LocationName
            title={locationName}
            onClick={() =>
              history.push(`/motor/${motor_id}/sensor/${sensor_id}/Pump - ${locationName}`)
            }
          >
            {truncatedLocationName}
          </LocationName>
          <StatusLine $isOnline={isOnline} $isFaulty={isFaulty} $isOff={isOff}>
            {statusLabel}
          </StatusLine>
        </PbMeta>

        <BadgeStack>
          <Badge $isOnline={isOnline} $isFaulty={isFaulty}>
            {isFaulty ? "faulty" : isOnline ? "online" : "offline"}
          </Badge>

          {!isOnline && (
            <PlainDuration>
              {formatOfflineDuration(pumpData?.motor_status?.updatedAt)}
            </PlainDuration>
          )}
        </BadgeStack>
      </PbInner>
    </PbBlock>
  );
}
